import Link from "next/link";
import DestinationStats from "@/components/DestinationStats";
import Image from "next/image";
import { cn, FALLBACK_FEATURE_IMAGE } from "@/lib/utils";
import { Typography } from "@/components/Typography";
import { TopDestinationItem } from "@/actions/destination.action";
import { memo, useMemo } from "react";

type DestinationCardProps = {
  destination: TopDestinationItem;
  className?: string;
  href: string;
};

const DestinationCard = ({
  destination,
  className,
  href = "#",
}: DestinationCardProps) => {
  const { name, thumbnail, summary } = destination;
  const newSummary = useMemo(() => {
    return Object.fromEntries(
      Object.entries(summary).filter(([_, value]) => value.total > 0),
    );
  }, [summary]);

  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "group flex flex-col gap-3 rounded-2xl shrink-0",
        className,
      )}
    >
      <div className="relative h-[140px] sm:h-[170px] w-full overflow-hidden rounded-2xl">
        <Image
          src={thumbnail ?? FALLBACK_FEATURE_IMAGE}
          alt={name}
          fill
          unoptimized
          sizes="(max-width: 640px) 280px, 356px"
          className="object-cover transform-gpu transition-transform duration-500 ease-out group-hover:scale-[1.03]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

        <Typography
          variant="h5"
          className="absolute bottom-2 left-2 lg:bottom-3 lg:left-3 text-white group-hover:text-secondary"
        >
          {name}
        </Typography>
      </div>

      <DestinationStats stats={newSummary as any} className="px-1" />
    </Link>
  );
};

export default memo(DestinationCard);
